import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLoaderData } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { CardPair } from "../components/Card";
import { Title } from "../components/Text";
import { Dots } from "../components/index";
import { useStore } from "../hooks/useStore";
import { useModalStore } from "../hooks/useModalStore";
import { NUM_PAIRS } from "@tinker-tots/shared/constants";
import { Loading } from "../components/Loading";
import { BeforeLeaveModal } from "./BeforeLeaveModal.jsx";

export function Game() {
  const { nextPath } = useLoaderData();
  // use a ref here for sychronous unblocking on completion
  const blockNavigationRef = useRef(true);
  const processedPairsRef = useRef(new Set());
  const loading = useStore((state) => state.loading);
  const cardPairs = useStore((state) => state.pairs);
  const selectCard = useStore((state) => state.selectCardFromPair);
  const triggerWarningViewed = useStore((state) => state.triggerWarningViewed);
  const openTriggerWarningModal = useModalStore(
    (state) => state.openTriggerWarningModal
  );
  const navigate = useNavigate();
  const onNext = useCallback(() => navigate(nextPath), [navigate, nextPath]);

  const completeAndNext = useCallback(() => {
    blockNavigationRef.current = false;
    onNext();
  }, [onNext]);

  const [pairNumber, setPairNumber] = useState(
    () => cardPairs.findIndex((pair) => !pair.some((c) => c.chosen)) || 0
  );

  useEffect(() => {
    if (pairNumber === -1 || pairNumber >= NUM_PAIRS) {
      completeAndNext();
    }
  }, [pairNumber, completeAndNext]);

  const { t } = useTranslation("game");

  useEffect(() => {
    if (!triggerWarningViewed) {
      openTriggerWarningModal();
    }
  }, [triggerWarningViewed, openTriggerWarningModal]);

  const onSelect = (choice) => {
    if (
      processedPairsRef.current.has(pairNumber) ||
      cardPairs[pairNumber].some((c) => c.chosen)
    ) {
      return;
    }
    processedPairsRef.current.add(pairNumber);
    selectCard({ chosen: choice, pairNumber });

    if (pairNumber < NUM_PAIRS - 1) {
      setPairNumber((n) => n + 1);
    } else {
      completeAndNext();
    }
  };

  if (loading || !cardPairs?.[pairNumber]) return <Loading />;
  return (
    <>
      <BeforeLeaveModal enableBlockerRef={blockNavigationRef} />
      <MobileGame
        pair={cardPairs[pairNumber]}
        pairNumber={pairNumber}
        onSelect={onSelect}
        t={t}
      />
      <DesktopGame
        pair={cardPairs[pairNumber]}
        pairNumber={pairNumber}
        onSelect={onSelect}
        t={t}
      />
    </>
  );
}

const MobileGame = ({ pair, pairNumber, onSelect, deselect, onNext, t }) => {
  const setPresentationTimeStamp = useStore(
    (state) => state.setPresentationTimeStamp
  );
  const setTaskDescriptionShown = useStore(
    (state) => state.setTaskDescriptionShown
  );

  useEffect(() => {
    setPresentationTimeStamp({
      setIndex: pairNumber,
      presentationTimeStamp: Date.now(),
    });
  }, [pairNumber, setPresentationTimeStamp]);

  return (
    <div className="sm:hidden max-w-343px m-auto">
      <div className="flex flex-col justify-center items-center">
        <div className="font-700 text-4.5 leading-6 text-center">
          {t("title")}
        </div>
        <div className="font-700 text-3.5 leading-3.5 py-2">
          {t("subtitle-mobile")}
        </div>
        <div className=" text-tinkerTots-gray-600 text-3 leading-3 text-center">
          {t("scenario-count", { current: pairNumber + 1, total: NUM_PAIRS })}
        </div>
      </div>
      <CardPair
        pair={pair}
        onSelect={onSelect}
        onShowDescription={() =>
          setTaskDescriptionShown({ setIndex: pairNumber })
        }
      />
    </div>
  );
};

const DesktopGame = ({ pair, pairNumber, onSelect, t }) => {
  const setPresentationTimeStamp = useStore(
    (state) => state.setPresentationTimeStamp
  );
  const setTaskDescriptionShown = useStore(
    (state) => state.setTaskDescriptionShown
  );

  useEffect(() => {
    setPresentationTimeStamp({
      setIndex: pairNumber,
      presentationTimeStamp: Date.now(),
    });
  }, [pairNumber, setPresentationTimeStamp]);

  return (
    <div className="hidden sm:block sm:w-868px m-auto sm:pt-18">
      <div className="flex flex-col justify-center items-center">
        <div className="flex justify-between items-center sm:w-full relative">
          <Title
            className="absolute left-1/2 transform -translate-x-1/2"
            text={t("title")}
          />
          <div className="font-sans text-tinkerTots-gray-600 text-3.5 leading-6 ml-auto pb-4.5">
            {t("scenario-count", { current: pairNumber + 1, total: NUM_PAIRS })}
          </div>
        </div>
        <div className="font-700 text-3.5 leading-3.5">
          {t("subtitle-desktop")}
        </div>
      </div>
      <CardPair
        pair={pair}
        onSelect={onSelect}
        onShowDescription={() =>
          setTaskDescriptionShown({ setIndex: pairNumber })
        }
      />
    </div>
  );
};
